import { useCallback, useMemo, useState } from "react";
import type { AnnotationSet } from "../../api/threads/liveMessages";
import { useUnsavedDraft } from "../processAuthoring/draft/useUnsavedDraft";
import { sameAnnotations, withAnnotation, withLabel, withoutAnnotation, type NewAnnotation } from "./annotationEditing";

/**
 * What the span view needs to edit a set: the selected span, and each edit. A span is
 * named by the span view's `id`, its index in the set.
 */
export interface AnnotationSetEditor {
  readonly selectedId: string | null;
  readonly select: (id: string | null) => void;
  readonly add: (annotation: NewAnnotation) => void;
  readonly relabel: (id: string, label: string) => void;
  readonly remove: (id: string) => void;
}

export interface AnnotationSetEditing {
  readonly edited: AnnotationSet;
  /** Whether the edited set differs from the reviewed one. */
  readonly changed: boolean;
  readonly editor: AnnotationSetEditor;
  /** Back to the reviewed set: every edit dropped (F96, one Discard). */
  readonly discard: () => void;
  /** The edits went to the server, with the step's answer or to the agent: leaving no longer asks. */
  readonly markSent: () => void;
}

/**
 * The span view's editor of `set`, controlled: each edit hands the next set to `onChange`, and
 * the owner keeps it (a review step, the artifact form, Documents). The editor keeps only which
 * span is selected. `null` without a set. Memoised, so the span view can skip renders while the
 * person types elsewhere.
 */
export function useAnnotationEditor(set: AnnotationSet | null, onChange: (next: AnnotationSet) => void): AnnotationSetEditor | null {
  const [selectedId, setSelectedId] = useState<string | null>(null);
  return useMemo<AnnotationSetEditor | null>(() => {
    if (set === null) {
      return null;
    }
    return {
      selectedId,
      select: setSelectedId,
      add: (annotation) => {
        // As in v1, the new span is selected, so its label can be changed at once.
        setSelectedId(String(set.annotations.length));
        onChange(withAnnotation(set, annotation));
      },
      relabel: (id, label) => { onChange(withLabel(set, Number(id), label)); },
      remove: (id) => {
        setSelectedId(null);
        onChange(withoutAnnotation(set, Number(id)));
      },
    };
  }, [set, selectedId, onChange]);
}

/**
 * A reviewed annotation set and the person's edits of it, as v1's review dialog held them;
 * `null` when nothing is reviewed. The reviewed set is frozen in the step's request, so
 * it is read once. `onEdit` runs on each change of the set. While the set differs from the
 * reviewed one, leaving asks first (`UnsavedDraftGuard`), as for every edit made in place (F96).
 */
export function useAnnotationSetEditing(reviewed: AnnotationSet | null, onEdit: () => void): AnnotationSetEditing | null {
  const [edited, setEdited] = useState(reviewed);
  const change = useCallback((next: AnnotationSet) => {
    setEdited(next);
    onEdit();
  }, [onEdit]);
  const editor = useAnnotationEditor(edited, change);
  const changed = useMemo(() => reviewed !== null && edited !== null && !sameAnnotations(reviewed, edited), [reviewed, edited]);
  const { markSaved } = useUnsavedDraft(changed ? edited : reviewed);
  if (edited === null || editor === null) return null;
  return {
    edited,
    changed,
    editor,
    discard: () => {
      editor.select(null);
      setEdited(reviewed);
      onEdit();
    },
    markSent: markSaved,
  };
}
